/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * DATA ACCESS LAYER : video_content_publications
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Ce fichier gère UNIQUEMENT les opérations CRUD sur la table `video_content_publications`.
 * 
 * Pour les opérations sur video_content, utiliser :
 * @see @/services/data/videoContent.ts
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type VideoContentPublication = Tables<"video_content_publications">;
export type VideoContentPublicationInsert = TablesInsert<"video_content_publications">;
export type VideoContentPublicationUpdate = TablesUpdate<"video_content_publications">;

// Extended type with republication info
export interface VideoContentPublicationWithRepublication extends VideoContentPublication {
  is_republication: boolean;
  social_post_id: number | null;
  published_at: string | null;
}

/**
 * Get all publications for a video content
 */
export const getPublicationsForVideo = async (videoContentId: string) => {
  try {
    const { data, error } = await supabase
      .from("video_content_publications")
      .select("*")
      .eq("video_content_id", videoContentId)
      .order("scheduled_at", { ascending: true });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Error fetching video publications:", error);
    return { success: false, error, data: [] };
  }
};

/**
 * Get all publications for a user within a date range (for calendar)
 */
export const getPublicationsForCalendar = async (
  userId: string,
  startDate?: Date,
  endDate?: Date
) => {
  try {
    let query = supabase
      .from("video_content_publications")
      .select(`
        *,
        video_content!inner(id, title, description, status, thumbnail_url, video_1_url, is_multihook, user_id)
      `)
      .eq("video_content.user_id", userId)
      .order("scheduled_at", { ascending: true });

    if (startDate) {
      query = query.gte("scheduled_at", startDate.toISOString());
    }
    if (endDate) {
      query = query.lte("scheduled_at", endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Error fetching publications for calendar:", error);
    return { success: false, error, data: [] };
  }
};

/**
 * Create a publication for a video content
 */
export const createPublication = async (publication: VideoContentPublicationInsert) => {
  try {
    const { data, error } = await supabase
      .from("video_content_publications")
      .insert(publication)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error creating publication:", error);
    return { success: false, error };
  }
};

/**
 * Create multiple publications at once
 */
export const createPublications = async (publications: VideoContentPublicationInsert[]) => {
  try {
    const { data, error } = await supabase
      .from("video_content_publications")
      .insert(publications)
      .select();

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Error creating publications:", error);
    return { success: false, error, data: [] };
  }
};

/**
 * Update a publication
 */
export const updatePublication = async (id: string, updates: VideoContentPublicationUpdate) => {
  try {
    const { data, error } = await supabase
      .from("video_content_publications")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error updating publication:", error);
    return { success: false, error };
  }
};

/**
 * Delete a publication
 */
export const deletePublication = async (id: string) => {
  try {
    const { error } = await supabase
      .from("video_content_publications")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error deleting publication:", error);
    return { success: false, error };
  }
};

/**
 * Delete all publications for a video content
 */
export const deleteAllPublicationsForVideo = async (videoContentId: string) => {
  try {
    const { error } = await supabase
      .from("video_content_publications")
      .delete()
      .eq("video_content_id", videoContentId);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error deleting publications for video:", error);
    return { success: false, error };
  }
};

/**
 * Replace all publications for a video with new ones
 */
export const replacePublicationsForVideo = async (
  videoContentId: string,
  publications: Array<{ channel: string; scheduled_at: string; status?: string }>
) => {
  try {
    // Delete existing publications
    await deleteAllPublicationsForVideo(videoContentId);

    // Insert new ones
    if (publications.length > 0) {
      const publicationsToInsert: VideoContentPublicationInsert[] = publications.map(pub => ({
        video_content_id: videoContentId,
        channel: pub.channel,
        scheduled_at: pub.scheduled_at,
        ...(pub.status ? { status: pub.status } : {}),
      }));

      const { data, error } = await supabase
        .from("video_content_publications")
        .insert(publicationsToInsert)
        .select();

      if (error) throw error;
      return { success: true, data: data || [] };
    }

    return { success: true, data: [] };
  } catch (error) {
    console.error("Error replacing publications for video:", error);
    return { success: false, error, data: [] };
  }
};

/**
 * Update scheduled_at for a publication
 */
export const updatePublicationScheduledAt = async (id: string, scheduledAt: string): Promise<{ success: boolean; error?: any }> => {
  try {
    const { error } = await supabase
      .from('video_content_publications')
      .update({ 
        scheduled_at: scheduledAt,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error updating publication scheduled_at:', error);
    return { success: false, error };
  }
};
